import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Sparkles } from "lucide-react";
import * as Icons from "lucide-react";
import { Button } from "./ui/button";
import { AppStoreBadges } from "./AppStoreBadges";

function FeatureChip({ label, icon }) {
  const Icon = Icons[icon] || Sparkles;
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-background/60 px-3 py-2">
      <Icon className="h-4 w-4 text-primary" />
      <span className="text-sm font-medium text-foreground">{label}</span>
    </div>
  );
}

export function ProductCard({ product, featured = false }) {
  return (
    <motion.article
      data-testid={`product-card-${product.slug}`}
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      className="relative overflow-hidden rounded-3xl border border-border bg-card p-8 md:p-10"
    >
      <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />

      <div className="relative flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          {product.status === "available" ? "Flagship product" : "In development"}
        </span>
        <span className="rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground">
          {product.category}
        </span>
      </div>

      <h3 className="relative mt-6 font-display text-3xl font-medium tracking-tight md:text-4xl">
        {product.name}
      </h3>
      <p className="relative mt-2 text-lg text-primary">{product.tagline}</p>
      <p className="relative mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
        {product.description}
      </p>

      <div className="relative mt-8 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
        {product.features.map((f) => (
          <FeatureChip key={f.label} label={f.label} icon={f.icon} />
        ))}
      </div>

      <div className="relative mt-8">
        <AppStoreBadges stores={product.stores} />
      </div>

      <div className="relative mt-8 flex flex-wrap items-center gap-4">
        <Button asChild className="rounded-full px-6" data-testid={`visit-${product.slug}`}>
          <a href={product.website} target="_blank" rel="noopener noreferrer">
            Visit {product.name}
            <ArrowUpRight className="h-4 w-4" />
          </a>
        </Button>
        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
          <CheckCircle2 className="h-4 w-4 text-primary" />
          Live website available
        </span>
      </div>
    </motion.article>
  );
}

export function ComingSoonCard({ title, description }) {
  return (
    <div
      data-testid="product-coming-soon"
      className="relative flex flex-col justify-center overflow-hidden rounded-3xl border border-dashed border-border bg-card/40 p-8 md:p-10"
    >
      <div className="grain absolute inset-0 opacity-40" />
      <Sparkles className="relative h-8 w-8 text-gold" />
      <h3 className="relative mt-5 font-display text-2xl font-medium tracking-tight">{title}</h3>
      <p className="relative mt-3 max-w-md text-base leading-relaxed text-muted-foreground">
        {description}
      </p>
      <Link
        to="/contact"
        className="relative mt-6 inline-flex w-fit items-center gap-1.5 text-sm font-medium text-primary hover:underline"
      >
        Partner or stay updated
        <ArrowUpRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
